import { Fragment, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatNum, manageSet, parseDate, toggleExpandContextMenu } from '../../utils/functions'
import { useOutsideClick } from '../../hooks'
import { FormSchema, QuestionAnswersSchema, QuestionSchema } from '../../utils/types/form'
import { SubmissionSchema } from '../../utils/types/submission'

const SUPABASE_STORAGE_URL = process.env.REACT_APP_SUPABASE_STORAGE_URL
const MAX_ANSWERS = 5

export const QuestionsTab: React.FC<{
  form: FormSchema | null
  submissions: SubmissionSchema[]
  allQuestions: QuestionSchema[]
}> = (props) => {
  const { form, submissions, allQuestions } = props

  const [questionSettings, setQuestionSettings] = useState<
    Record<string, { offset: number; isViewingAll: boolean }>
  >({})
  const [isFilterOptionsOpen, setIsFilterOptionsOpen] = useState(false)
  const [filterOptions, setFilterOptions] = useState<Set<string>>(new Set())
  const [isFilterOptionsAllSelected, setIsFilterOptionsAllSelected] = useState(false)
  const [allQuestionAnswers, setAllQuestionAnswers] = useState<
    Map<string, QuestionAnswersSchema[]>
  >(new Map())
  const [isContextMenuExpanded, setIsContextMenuExpanded] = useState<Record<string, boolean>>({})
  const questionOptionsTriggerRef = useRef(null)
  const questionOptionsContextRef = useRef(null)

  useOutsideClick({ element: questionOptionsContextRef, trigger: questionOptionsTriggerRef }, () =>
    setIsFilterOptionsOpen(false)
  )

  const navigate = useNavigate()

  useEffect(() => {
    const questionAnswers: Map<string, QuestionAnswersSchema[]> = new Map()

    submissions.forEach(({ _id: submissionId, sections, submittedOn }) => {
      sections.forEach(({ questions }) => {
        questions.forEach(({ id, type, answer }) => {
          const newAnswer: QuestionAnswersSchema = {
            submissionId,
            submittedOn,
            questionId: id,
            type,
            answer
          }

          const answers = questionAnswers.get(id)
          answers
            ? questionAnswers.set(id, [...answers, newAnswer])
            : questionAnswers.set(id, [newAnswer])
        })
      })
    })

    setAllQuestionAnswers(questionAnswers)
  }, [submissions])

  useEffect(() => {
    setFilterOptions((filterOptions) => {
      const questionIdSet = new Set(filterOptions)

      allQuestions.forEach(({ id }) => {
        questionIdSet.add(id)
      })

      return questionIdSet
    })
  }, [allQuestions])

  useEffect(() => {
    const isAllSelected = allQuestions.length === filterOptions.size
    setIsFilterOptionsAllSelected(isAllSelected)

    // eslint-disable-next-line
  }, [filterOptions])

  const getTrendIcon = (questionAnswers: QuestionAnswersSchema[], index: number): string => {
    let icon = ''
    const currentValue = Number(questionAnswers[index]?.answer)
    let prevValue

    for (let i = 0; i < questionAnswers.length; i++) {
      const answer = questionAnswers[index + 1 + i]?.answer
      if (answer) {
        prevValue = Number(answer)
        break
      }
    }

    if (index < questionAnswers.length - 1) {
      icon =
        !isNaN(currentValue) && prevValue
          ? currentValue < prevValue
            ? 'trending_down'
            : currentValue > prevValue
              ? 'trending_up'
              : 'trending_flat'
          : ''
    }

    return icon
  }

  const toggleShowAllAnswers = (questionId: string) => {
    setQuestionSettings({
      ...questionSettings,
      [questionId]: {
        ...questionSettings[questionId],
        isViewingAll: !questionSettings[questionId]?.isViewingAll
      }
    })
  }

  const handleNextPage = (questionId: string) => {
    setQuestionSettings({
      ...questionSettings,
      [questionId]: {
        ...questionSettings[questionId],
        offset: questionSettings[questionId]?.offset ? questionSettings[questionId].offset + 1 : 1
      }
    })
  }

  const handlePrevPage = (questionId: string) => {
    setQuestionSettings({
      ...questionSettings,
      [questionId]: {
        ...questionSettings[questionId],
        offset: questionSettings[questionId]?.offset ? questionSettings[questionId].offset - 1 : 0
      }
    })
  }

  const handleQuestionChange = (questionId: string) => {
    setFilterOptions((filterOptions) => {
      const questionIdSet = new Set(filterOptions)

      if (questionId) {
        manageSet(questionIdSet, questionId)
      } else {
        if (isFilterOptionsAllSelected) {
          questionIdSet.clear()
        } else {
          allQuestions.forEach(({ id }) => {
            questionIdSet.add(id)
          })
        }
      }

      return questionIdSet
    })
  }

  const handleExpandContextMenu = (ref: HTMLDivElement) => {
    toggleExpandContextMenu(ref, isContextMenuExpanded, setIsContextMenuExpanded)
  }

  return (
    <>
      {form && (
        <>
          <div className='title_container'>
            <h2>View by question</h2>
          </div>
          {!!submissions.length && (
            <div className='actions_container'>
              <div className='actions-select actions-select-filter'>
                <div>
                  <span className='material-symbols-rounded material-symbols-sm'>event_list</span>
                  {!isFilterOptionsAllSelected && (
                    <span className='on-indicator' title='Some filters have been applied'>
                      ●
                    </span>
                  )}
                </div>
                <div className='actions_series'>
                  <div>
                    <div
                      id='question_options_trigger'
                      className='div-with-icon'
                      onClick={() => setIsFilterOptionsOpen(!isFilterOptionsOpen)}
                      ref={questionOptionsTriggerRef}>
                      <span id='question_options_trigger'>Show filters</span>
                      <span id='question_options_trigger' className='material-symbols-rounded'>
                        {isFilterOptionsOpen ? 'arrow_drop_down' : 'arrow_right'}
                      </span>
                    </div>
                    {isFilterOptionsOpen && (
                      <div
                        className={`context_menu context_menu-left context_menu-down context_menu-${
                          isContextMenuExpanded['questionOptionsContext'] ? 'lg' : 'md'
                        }`}
                        id='questionOptionsContext'
                        ref={questionOptionsContextRef}>
                        <div className='context_menu-grid context-header'>
                          <div
                            onClick={() =>
                              questionOptionsContextRef.current &&
                              handleExpandContextMenu(questionOptionsContextRef.current)
                            }>
                            <h4>Values</h4>
                            <span className='material-symbols-rounded'>
                              {isContextMenuExpanded['questionOptionsContext']
                                ? 'collapse_content'
                                : 'expand_content'}
                            </span>
                          </div>
                          <input
                            id='checkbox_all'
                            type='checkbox'
                            checked={isFilterOptionsAllSelected}
                            onChange={() => handleQuestionChange('')}
                          />
                          <label htmlFor='checkbox_all' className='highlight-styled-hover'>
                            All
                          </label>
                        </div>
                        {form.sections.map(({ id: sectionId, name, questions }) => (
                          <div className='context_menu-grouped' key={sectionId}>
                            <h5>{name}</h5>
                            <div className='context_menu-grid'>
                              {questions.map(({ id: questionId, statement }) => (
                                <Fragment key={questionId}>
                                  <input
                                    id={`checkbox_${questionId}`}
                                    type='checkbox'
                                    checked={filterOptions.has(questionId)}
                                    onChange={() => handleQuestionChange(questionId)}
                                  />
                                  <label
                                    htmlFor={`checkbox_${questionId}`}
                                    className='highlight-styled-hover'>
                                    {statement}
                                  </label>
                                </Fragment>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {!!submissions.length ? (
            <>
              <div className={`form_content theme-content-${form.meta.colour}`}>
                {form.sections.map((section, index) => {
                  const hasQuestion = section.questions.some(({ id }) => filterOptions.has(id))
                  if (!hasQuestion) {
                    return <Fragment key={index}></Fragment>
                  }

                  return (
                    <section key={section.id}>
                      {section.meta?.showHeader && (
                        <div className='header'>
                          {section.name && <h2>{section.name}</h2>}
                          <p>{section.description}</p>
                        </div>
                      )}
                      {section.questions.map((question, index) => {
                        if (!filterOptions.has(question.id)) {
                          return <Fragment key={index}></Fragment>
                        }

                        const questionAnswers = allQuestionAnswers.get(question.id)
                        const totalValidResponses =
                          questionAnswers?.filter(({ answer }) => answer).length || 0
                        const totalResponses = submissions.length
                        const responseRate = ((totalValidResponses / totalResponses) * 100).toFixed(
                          0
                        )

                        const settings = questionSettings[question.id]
                        const totalPages = questionAnswers
                          ? Math.ceil(questionAnswers.length / MAX_ANSWERS)
                          : 0

                        return (
                          <div className='section_question' key={index}>
                            <span>{index + 1}.</span>
                            <p>
                              <span className='bold'>{question.statement}</span>
                              {question.description && <em> ({question.description})</em>}
                              {question.required && <span className='required'>*</span>}
                            </p>
                            <div />
                            <span className='section_question-details'>
                              <em>{`Responses: ${totalValidResponses} / ${totalResponses}`}</em>
                              {' ● '}
                              <em>{`Response rate: ${responseRate}%`}</em>
                              {questionAnswers && (
                                <>
                                  {questionAnswers.length > MAX_ANSWERS && (
                                    <div className='answer_actions'>
                                      {!settings?.isViewingAll && (
                                        <>
                                          <span>
                                            {settings?.offset ? settings.offset + 1 : 1}/
                                            {totalPages}
                                          </span>
                                          {settings?.offset - 1 >= 0 ? (
                                            <span
                                              className='material-symbols-rounded'
                                              onClick={() => {
                                                handlePrevPage(question.id)
                                              }}>
                                              keyboard_arrow_left
                                            </span>
                                          ) : (
                                            <span className='material-symbols-rounded disabled'>
                                              keyboard_arrow_left
                                            </span>
                                          )}
                                          {!settings?.offset || settings.offset + 1 < totalPages ? (
                                            <span
                                              className='material-symbols-rounded'
                                              onClick={() => {
                                                handleNextPage(question.id)
                                              }}>
                                              keyboard_arrow_right
                                            </span>
                                          ) : (
                                            <span className='material-symbols-rounded disabled'>
                                              keyboard_arrow_right
                                            </span>
                                          )}
                                        </>
                                      )}
                                      {settings?.isViewingAll && (
                                        <span
                                          className='material-symbols-rounded hidden'
                                          onClick={() => {
                                            toggleShowAllAnswers(question.id)
                                          }}>
                                          keyboard_arrow_up
                                        </span>
                                      )}
                                      <span
                                        className='span-a'
                                        onClick={() => {
                                          toggleShowAllAnswers(question.id)
                                        }}>
                                        {settings?.isViewingAll ? 'Show less' : 'Show all'}
                                      </span>
                                    </div>
                                  )}
                                </>
                              )}
                            </span>
                            {questionAnswers && (
                              <>
                                <div />
                                <div className='section_question-list'>
                                  {questionAnswers.map(
                                    (
                                      { submissionId, submittedOn, questionId, type, answer },
                                      index
                                    ) => {
                                      const offset = questionSettings[questionId]?.offset || 0

                                      const startWindow = offset * MAX_ANSWERS
                                      const endWindow = startWindow + MAX_ANSWERS

                                      if (
                                        (index < startWindow || index >= endWindow) &&
                                        !questionSettings[questionId]?.isViewingAll
                                      ) {
                                        return <Fragment key={submittedOn} />
                                      } else {
                                        let icon = ''

                                        if (
                                          type === 'number' ||
                                          (type === 'dropdown' &&
                                            question.meta?.dropdownOptions?.values.every(
                                              (option) => !Number.isNaN(Number(option))
                                            ))
                                        ) {
                                          icon = getTrendIcon(questionAnswers, index)
                                        }

                                        return (
                                          <div key={submittedOn}>
                                            <div className='section_question-list-content'>
                                              {((typeof answer === 'string' ||
                                                typeof answer === 'number') &&
                                                answer) ||
                                              (Array.isArray(answer) && answer.length) ? (
                                                <span className='highlight highlight-answer'>
                                                  {question.type === 'file' ? (
                                                    <>
                                                      <em className='bold'>File:</em>{' '}
                                                      <a
                                                        href={`${SUPABASE_STORAGE_URL}/${answer}`}
                                                        className='underline'
                                                        target='_blank'
                                                        rel='noreferrer'>
                                                        {answer}
                                                      </a>
                                                    </>
                                                  ) : (
                                                    <>
                                                      {question.type === 'checkbox' &&
                                                      Array.isArray(answer)
                                                        ? answer.join(', ')
                                                        : question.type === 'date'
                                                          ? typeof answer === 'number' &&
                                                            parseDate(answer, 'short_date')
                                                          : question.type === 'number'
                                                            ? formatNum(answer as number)
                                                            : answer}
                                                    </>
                                                  )}
                                                </span>
                                              ) : (
                                                <span>-</span>
                                              )}
                                              {answer && icon && (
                                                <span
                                                  className={`material-symbols-rounded 
                                                  ${
                                                    icon === 'trending_flat'
                                                      ? 'flat'
                                                      : icon === 'trending_up'
                                                        ? 'gain'
                                                        : 'loss'
                                                  }`}>
                                                  {icon}
                                                </span>
                                              )}
                                            </div>
                                            <span
                                              className='section_answer-details'
                                              onClick={() =>
                                                navigate(
                                                  `/form/${form._id}/submission/${submissionId}`,
                                                  { viewTransition: true }
                                                )
                                              }>
                                              <em>#{questionAnswers.length - index}</em>
                                              <em className='span-a'>
                                                {`${
                                                  form.isPublic
                                                    ? 'View response'
                                                    : `View entry: ${parseDate(
                                                        submittedOn,
                                                        'short_date'
                                                      )}`
                                                }`}
                                              </em>
                                            </span>
                                          </div>
                                        )
                                      }
                                    }
                                  )}
                                </div>
                                <div />
                                {settings?.isViewingAll && (
                                  <span
                                    className='span-a'
                                    onClick={() => {
                                      setQuestionSettings({
                                        ...questionSettings,
                                        [question.id]: {
                                          ...settings,
                                          isViewingAll: false
                                        }
                                      })
                                    }}>
                                    Show less
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        )
                      })}
                    </section>
                  )
                })}
              </div>
            </>
          ) : (
            <span>No {form?.isPublic ? 'responses' : 'entries'} yet...</span>
          )}
        </>
      )}
    </>
  )
}
